/**
 * Os selos de confiança.
 *
 * O painel anterior apresentava 478 licitações como cidadãs iguais. Não eram:
 * 174 tinham sido derrubadas por palavra no objeto antes de qualquer leitura,
 * só 33 receberam análise profunda, e em 96 o edital nunca pôde ser baixado —
 * a IA julgou pelos metadados. Nada disso chegava à tela, então um score de 65
 * lido do edital inteiro parecia idêntico a um score de 65 chutado do resumo.
 *
 * Estes componentes existem para que o número nunca apareça sozinho.
 */
import type {
  EstadoDecisao,
  EstadoEdital,
  EtapaDaDisputa,
  Etiqueta,
  MotivoAba,
  Nivel,
  Recomendacao,
} from "../api/cliente";
import css from "./Selos.module.css";

/**
 * POR QUE ESTA ABA, quando o score sozinho nao explica.
 *
 * Pedido da gestao, 2026-09-16: score 91 em "Passar" (contrato abaixo do piso)
 * e score 55 em "Avaliar" (GO do edital) pareciam defeito a quem nao conhece
 * as regras. A etiqueta diz o motivo no cartao; a frase inteira fica no hover
 * e, no painel, por extenso. O texto vem do servidor, que e onde a regra mora.
 */
/** A licitacao foi descartada pelo funil — vencida, suspensa, revogada ou
 * anulada (decisao de 2026-09-22)? Entao o `descartar` dela veio do prazo ou
 * da situacao, e nao do score nem do edital, e nada que compare score com
 * triagem ou com a leitura do edital tem o que dizer. */
export function descartadaPeloFunil(motivo?: MotivoAba | null): boolean {
  return motivo?.tipo === "descartada_prazo" || motivo?.tipo === "descartada_situacao";
}

/**
 * AS ÚNICAS ETIQUETAS DA TELA (o Pedro, 14/10): "as únicas labels que devem
 * existir são valor abaixo do piso de 250 mil e prazo vencido". Quem diz se
 * cada uma vale é o servidor (`acervo.etiquetas`), pelo fato dela; o texto
 * também vem de lá.
 */
export function Etiquetas({ itens }: { itens?: Etiqueta[] | null }) {
  if (!itens?.length) return null;
  return (
    <>
      {itens.map((e) => (
        <span key={e.tipo}
              className={`${css.selo} ${e.tipo === "prazo_vencido" ? css.nao : css.atencao}`}>
          {e.texto}
        </span>
      ))}
    </>
  );
}

export function EtiquetaAba({ motivo }: { motivo?: MotivoAba | null }) {
  if (!motivo) return null;
  const classe =
    motivo.tipo === "edital_nogo" ? css.nao
    : motivo.tipo === "descartada_prazo" || motivo.tipo === "descartada_situacao"
      ? css.nao
    : motivo.tipo === "abaixo_do_piso" ? css.atencao
    // O edital enviado que abre apesar do score abaixo de 50 (2026-10-05):
    // âmbar, como o piso de valor — é uma regra explicando onde ele está.
    : motivo.tipo === "enviado_abaixo_de_50" ? css.atencao
    // A que aguarda a leitura do edital (D17, 2026-10-05): também uma regra
    // explicando onde ela está — e por que o cartão não tem score.
    : motivo.tipo === "aguardando_leitura" ? css.atencao
    // A fora do escopo (D21, 2026-10-08): em Descartadas com qualquer score,
    // porque a triagem leu o objeto e disse que não é do Nectar.
    : motivo.tipo === "fora_do_escopo" ? css.atencao
    // E a fora do escopo pela LEITURA DO EDITAL (D32, 2026-10-16): o NO-GO
    // conferido da pré-análise por "fora do escopo" também a leva para lá.
    : motivo.tipo === "edital_fora_do_escopo" ? css.atencao
    : motivo.tipo === "valor_nao_informado" ? css.neutro
    : css.forte;
  return (
    <span className={`${css.selo} ${classe}`} title={motivo.ajuda}>
      {motivo.texto}
    </span>
  );
}

/** DE ONDE SAIU O SCORE. Só aparece quando o sistema pontua pelo edital
 * (`score_lado` vem nulo com a fonte `objeto`). Decisão do Pedro,
 * 2026-09-22: "score pelo objeto, marcado".
 *
 * E O LADO DO EDITAL TAMBÉM É MARCADO (decisão de 30/09): a D6 vira como
 * passo intermediário, com a ficha técnica escrita do TRECHO técnico — de 3%
 * a 15% do edital —, e não do edital inteiro. Chamar isso de "pelo edital"
 * afirmaria uma leitura que não houve. O selo sai quando a ficha passar a
 * vir da leitura integral certificada (Fase 4 do plano). */
/* Com a matriz ligada (decisão do Pedro, 01/10), quem não tem o edital lido
 * inteiro e certificado fica com o score do objeto de sempre, com a triagem
 * na conta — e o trecho sai do caminho. */
export const LADO_OBJETO =
  "O score desta licitação saiu da FRASE DO OBJETO, e não do edital: o " +
  "edital dela ainda não foi lido inteiro e certificado — ou não está " +
  "publicado, ou a fila da leitura ainda não chegou nele. É o score de " +
  "antes da leitura integral. As licitações com o edital lido inteiro são " +
  "pontuadas pela matriz (cada exigência do edital contra os atestados), e " +
  "os dois números não se comparam diretamente.";

const LADO_TRECHO =
  "O score desta licitação saiu da ficha técnica do edital cruzada com " +
  "os atestados — mas a ficha, por enquanto, é escrita a partir do TRECHO " +
  "TÉCNICO (objeto, execução e qualificação técnica: de 3% a 15% do " +
  "edital), e não do edital inteiro. A leitura integral, certificada, vai " +
  "substituí-la.";

/** Da LEITURA INTEGRAL CERTIFICADA (Fase 4A): o selo fraco sai — é a
 * leitura que o selo do trecho prometia —, e a legenda diz de onde veio. */
const LADO_LEITURA =
  "O score desta licitação saiu da LEITURA INTEGRAL CERTIFICADA do edital: " +
  "a ficha técnica escrita a partir do edital inteiro, cruzada com os " +
  "atestados.";

const LADO_MATRIZ =
  "O score desta licitação saiu da LEITURA INTEGRAL CERTIFICADA do edital: " +
  "a cobertura da matriz — quantas exigências centrais do edital algum " +
  "atestado prova, com a prova conferida no texto dele.";

type Lado = "objeto" | "edital" | "leitura" | "matriz" | null;

/* O SELO "score pelo objeto" SAIU DA TELA (pedido do Pedro, 08/10, na noite
 * em que o score passou a sair do cruzamento): com a fonte `edital` ligada,
 * toda licitação ainda não lida vinha marcada — mais de duas mil. Nem o selo
 * nem a legenda aparecem para o lado `objeto`; o texto fica aqui, e o lado
 * continua vindo da API (`score_lado`). */

/** A frase do selo, para o toque, que não tem hover (a legenda do painel).
 * Mesma razão de `explicaEdital`: uma cópia só do texto. */
export const explicaLadoDoScore = (lado?: Lado) =>
  lado === "edital" ? LADO_TRECHO
  : lado === "leitura" ? LADO_LEITURA
  : lado === "matriz" ? LADO_MATRIZ
  : null;

export function SeloLadoDoScore({ lado }: { lado?: Lado }) {
  if (lado !== "edital") return null;
  return (
    <span className={`${css.selo} ${css.fraco}`} title={LADO_TRECHO}>
      score pelo trecho técnico
    </span>
  );
}

export function SeloRecomendacao({ valor }: { valor?: Recomendacao | null }) {
  if (!valor) return <span className={`${css.selo} ${css.sem}`}>sem análise</span>;
  const classe =
    valor === "participar" ? css.ok : valor === "avaliar" ? css.atencao : css.nao;
  return <span className={`${css.selo} ${classe}`}>{valor}</span>;
}

/**
 * A OPINIÃO DO SISTEMA quando uma pessoa decidiu outra coisa (D11, decisão do
 * Pedro em 2026-10-02): "nesses momentos a decisão superior prevalece em
 * relação ao sistema". A recomendação segue a decisão; este selo diz o que o
 * sistema concluiria sozinho, para a divergência ficar à vista em vez de
 * sumir. Nada quando não há decisão, ou quando as duas concordam.
 */
export function SeloOpiniaoDoSistema({
  decisao,
  recomendacao,
  sistema,
}: {
  decisao?: EstadoDecisao | null;
  recomendacao?: Recomendacao | null;
  sistema?: Recomendacao | null;
}) {
  if (!decisao || decisao === "pendente" || !sistema || sistema === recomendacao) {
    return null;
  }
  return (
    <span
      className={`${css.selo} ${css.divergencia}`}
      title={
        `Uma pessoa ${decisao === "aprovada" ? "aprovou" : "reprovou"} esta ` +
        "licitação, e a decisão dela prevalece sobre o sistema. Sozinho, pela " +
        `leitura do edital, pelo parecer ou pelo score, o sistema diria "${sistema}".`
      }
    >
      sistema: {sistema}
    </span>
  );
}

/**
 * A ETAPA DEPOIS DA DECISÃO (D13), no cartão da lista. Nada na aprovada sem
 * mais nada: a aba já diz. "em preparação" é o que distingue, dentro de
 * Aprovadas, a que alguém já começou a montar.
 */
export function SeloEtapa({ etapa }: { etapa?: EtapaDaDisputa | null }) {
  if (!etapa || etapa === "aprovada") return null;
  const [texto, classe, dica] = {
    preparacao: ["em preparação", "", "A documentação desta licitação está sendo preparada."],
    em_disputa: ["em disputa", css.divergencia, "Alguém informou que a Nectar entrou nesta disputa."],
    vencida: ["vencida", `${css.decidida} ${css.ok}`, "A Nectar venceu esta licitação."],
    perdida: [
      "perdida",
      `${css.decidida} ${css.nao}`,
      "A Nectar perdeu esta disputa, ou o prazo passou sem a disputa ser informada.",
    ],
  }[etapa];
  return (
    <span className={`${css.selo} ${classe}`} title={dica}>
      {texto}
    </span>
  );
}

/**
 * O item do acervo que NÃO é atestado, ao lado do código dele.
 *
 * O projeto executado que a gestão informou (D14, migração 0124) entra no
 * cruzamento e pontua como um atestado — é para isso que ele está no acervo —,
 * e o documento que a comissão confere não está no sistema. Em toda lista em
 * que um código do acervo aparece como apoio de uma licitação, quem lê precisa
 * saber disso sem abrir a ficha: montar proposta sobre atestado que não existe
 * é o pior erro que esta tela pode induzir.
 *
 * Declaração e nota fiscal ganham o mesmo selo: também cruzam, e também não
 * comprovam capacidade.
 */
const NAO_E_ATESTADO: Record<string, [string, string]> = {
  projeto: [
    "projeto, sem atestado",
    "Projeto executado que a gestão informou. O atestado dele não está no sistema: antes de usá-lo numa proposta, é preciso obtê-lo com o cliente.",
  ],
  declaracao: ["declaração", "Declaração, e não atestado: não comprova capacidade técnica."],
  "nota fiscal": ["nota fiscal", "Nota fiscal, e não atestado: não comprova capacidade técnica."],
};

export function SeloNaoAtestado({ natureza }: { natureza?: string | null }) {
  const achado = natureza ? NAO_E_ATESTADO[natureza] : undefined;
  if (!achado) return null;
  return (
    <span className={`${css.selo} ${css.atencao}`} title={achado[1]}>
      {achado[0]}
    </span>
  );
}

type Aderencia = "alta" | "media" | "baixa" | "nenhuma";

/**
 * Quando o score e a triagem discordam.
 *
 * A recomendação vem do score; a aderência vem da IA lendo o objeto. São
 * perguntas diferentes, e quando dão respostas diferentes isso não é ruído —
 * é a licitação que mais merece olho humano, porque as duas únicas coisas que
 * o sistema sabe fazer sozinho não chegaram ao mesmo lugar.
 *
 * Antes deste selo a discordância aparecia como uma inconsistência muda: uma
 * licitação de score 84 marcada "participar" ao lado de uma de 73 igualmente
 * marcada, com a de 84 tendo aderência apenas "media" e nada na tela dizendo
 * por quê. Quem olhasse concluiria que a tela estava errada.
 *
 * Dois sentidos, e eles pedem ações opostas:
 *
 * - **score puxou**: passou do limiar sem a IA ter dito "alta". Vale conferir
 *   se a triagem foi conservadora demais. Foi o caso medido em 2026-09: a 2ª
 *   maior semelhança de 196 licitações, com a IA respondendo "media".
 * - **triagem puxou**: a IA disse "alta" e o score não alcançou o limiar.
 *   Vale conferir se falta atestado no acervo para sustentar o que ela viu.
 *
 * Devolve `null` quando concordam, que é o caso comum — selo que aparece
 * sempre não destaca nada.
 */
function divergencia(
  recomendacao?: Recomendacao | null,
  aderencia?: Aderencia | null,
  motivo?: MotivoAba | null,
): { rotulo: string; titulo: string } | null {
  // Sem um dos dois lados não há discordância a afirmar — só ausência.
  if (!recomendacao || !aderencia) return null;
  // A DESCARTADA PELO FUNIL nao diverge: o `descartar` dela e do prazo. Sem
  // isto, uma vencida de score 95 e aderencia alta ganhava "triagem puxou",
  // afirmando que o score nao alcancou o limiar.
  if (descartadaPeloFunil(motivo)) return null;

  if (recomendacao === "participar" && aderencia !== "alta") {
    return {
      rotulo: "score puxou",
      titulo:
        `A recomendação é participar, mas a triagem classificou a aderência como "${aderencia}", ` +
        "não “alta”. Quem levou a licitação até aqui foi outra coisa — a afinidade com os " +
        "atestados, a leitura do edital ou o parecer do agente. " +
        "Vale conferir se a IA foi conservadora demais ao ler o objeto.",
    };
  }

  if (recomendacao !== "participar" && aderencia === "alta") {
    return {
      rotulo: "triagem puxou",
      titulo:
        "A triagem classificou a aderência como “alta”, mas a recomendação não é participar. " +
        "O motivo pode ser o score, o valor do contrato, a leitura do edital ou o parecer — " +
        "veja a etiqueta da aba e a análise. Vale conferir se falta atestado no acervo para " +
        "sustentar o que a IA viu no objeto.",
    };
  }

  return null;
}

/** A frase do selo de divergência, para o toque (ver `explicaLadoDoScore`). */
export const explicaDivergencia = (
  recomendacao?: Recomendacao | null,
  aderencia?: Aderencia | null,
  motivo?: MotivoAba | null,
) => divergencia(recomendacao, aderencia, motivo)?.titulo ?? null;

export function SeloDivergencia({
  recomendacao,
  aderencia,
  motivo,
}: {
  recomendacao?: Recomendacao | null;
  aderencia?: Aderencia | null;
  motivo?: MotivoAba | null;
}) {
  const d = divergencia(recomendacao, aderencia, motivo);
  if (!d) return null;
  return (
    <span className={`${css.selo} ${css.divergencia}`} title={d.titulo}>
      {d.rotulo}
    </span>
  );
}

/* SOBRE QUANTO cada licitação foi julgada — e a resposta a "dá para comparar
   estas duas?". O texto anterior dizia que a triagem lia "um recorte do
   edital", e isso nunca foi verdade: ela lê o OBJETO, uma ou duas frases. A
   diferença entre os dois níveis é de duas ordens de grandeza, e quem compara
   sem saber disso compara errado. */
const NIVEL: Record<Nivel, { rotulo: string; titulo: string }> = {
  triagem: {
    rotulo: "só o objeto",
    titulo:
      "A IA leu apenas o OBJETO — uma ou duas frases — e o cruzou com o " +
      "acervo. Serve para ordenar a fila, não para decidir: a licitação de " +
      "maior score desta base tirou 84 de 100 pontos assim, e a leitura do " +
      "edital a classificou NO-GO. As palavras que a desclassificavam não " +
      "estavam no objeto; estavam nas 217 páginas do edital.",
  },
  profunda: {
    rotulo: "pré-análise do edital",
    titulo:
      "A IA leu o EDITAL — até 1 milhão de caracteres, incluindo termo de " +
      "referência e anexos — e extraiu exigências de habilitação, prazos, " +
      "riscos e custos. Quando esta leitura discorda do score, é ela que vale: " +
      "é a única das duas que viu o documento. Se ela NÃO leu o edital " +
      "inteiro (documento cortado, ilegível ou com lacuna), o cartão diz, e o " +
      "painel lista o que faltou.",
  },
};

/** A frase do selo de nível, para o toque (ver `explicaLadoDoScore`). */
export const explicaNivel = (valor?: Nivel | null) => (valor ? NIVEL[valor].titulo : null);

export function SeloNivel({ valor }: { valor?: Nivel | null }) {
  if (!valor) return null;
  const n = NIVEL[valor];
  return (
    <span
      className={`${css.selo} ${valor === "profunda" ? css.forte : css.fraco}`}
      title={n.titulo}
    >
      {n.rotulo}
    </span>
  );
}

/** O ESTADO DO EDITAL. Desde 30/09 (D10), "lido" é LIDO INTEIRO — todo
 * arquivo publicado virou texto, sem lacuna —, e o que não fechou diz o que
 * faltou. Antes, qualquer documento com algum texto fazia a tela dizer "edital
 * lido", com o termo de referência com senha ou 300 páginas escaneadas sem
 * OCR. A seção Edital do painel e a auditoria (`nectuno-leitura
 * --auditoria`) dizem o que ficou de fora. */
const EDITAL: Record<EstadoEdital, { rotulo: string; titulo: string; classe: string }> = {
  lido: {
    rotulo: "edital lido inteiro",
    titulo:
      "Todo arquivo publicado foi baixado e virou texto inteiro — sem página " +
      "escaneada sem OCR, sem anexo com senha, sem link que não abriu.",
    classe: "ok",
  },
  lido_com_lacunas: {
    rotulo: "edital lido, com lacunas",
    titulo:
      "O edital foi lido, mas algum arquivo não tem como virar texto (senha, " +
      "arquivo corrompido, formato sem leitor), confirmado em dois dias. O que " +
      "faltou está na seção Edital. Uma leitura com lacuna nunca vira GO.",
    classe: "aviso",
  },
  incompleto: {
    rotulo: "leitura incompleta",
    titulo:
      "Algum arquivo ainda não foi lido inteiro: OCR pela metade (continua na " +
      "próxima rodada), link que falhou, ou teto da configuração. O que faltou " +
      "está na seção Edital.",
    classe: "aviso",
  },
  lendo: {
    rotulo: "leitura em andamento",
    titulo:
      "Parte dos arquivos publicados já virou texto, e o resto está na fila da " +
      "etapa de documentos. Até fechar, o edital não conta como lido.",
    classe: "neutro",
  },
  sem_caminho: {
    rotulo: "sem caminho até o edital",
    titulo:
      "Nenhuma fonte desta licitação leva ao edital (o registro não traz o " +
      "link do PNCP), e ninguém o enviou à mão. O jeito é obter o edital por " +
      "fora e mandá-lo pela tela Editais.",
    classe: "aviso",
  },
  apagado: {
    rotulo: "edital lido e apagado",
    titulo:
      "O PDF foi baixado, analisado, e depois removido do disco por a " +
      "licitação ter sido descartada. A análise usou o texto; o arquivo pode " +
      "ser rebaixado se o perfil mudar.",
    classe: "neutro",
  },
  indisponivel: {
    rotulo: "edital não baixado",
    titulo:
      "A fonte externa recusou o download — normalmente portal que exige " +
      "login próprio ou JavaScript. A IA julgou apenas pelos metadados.",
    classe: "aviso",
  },
  publicado: {
    rotulo: "edital publicado",
    titulo:
      "O PNCP publicou arquivo para esta licitação, e o link está na seção " +
      "Edital. O PDF ainda não foi baixado nem lido — a análise usou apenas " +
      "os metadados.",
    classe: "ok",
  },
  ausente: {
    rotulo: "sem edital publicado",
    titulo:
      "Perguntamos ao PNCP e a compra não tem arquivo publicado. A IA julgou " +
      "apenas pelos metadados: órgão, objeto, modalidade e prazos.",
    classe: "aviso",
  },
  enviado: {
    rotulo: "edital enviado à mão",
    titulo:
      "Alguém mandou o edital pela tela Editais. O documento está no banco, " +
      "com o texto extraído, e é dele que saem o trecho técnico, a ficha e a " +
      "pré-análise — não se depende do PNCP publicar nada.",
    classe: "ok",
  },
  nao_perguntado: {
    rotulo: "edital não consultado",
    titulo:
      "Ninguém perguntou à fonte se há edital publicado — rode " +
      "`nectuno-dossie`. Não é o mesmo que não haver edital, e por isso não " +
      "se parece com 'sem edital publicado'. As licitações do Licitmais ficam " +
      "sempre aqui: o portal não expõe a lista de arquivos.",
    classe: "neutro",
  },
};

/** O texto do selo, para quem precisa da frase e não da etiqueta.
 *
 * Existe para que a seção Edital do painel não mantenha uma segunda cópia
 * dessas explicações. Havia duas, e elas já divergiam. */
export const explicaEdital = (v: EstadoEdital) => EDITAL[v].titulo;
/** O rótulo curto do mesmo estado ("edital lido inteiro"), para o resumo do detalhe. */
export const rotuloEdital = (v: EstadoEdital) => EDITAL[v].rotulo;

export function SeloEdital({ valor }: { valor: EstadoEdital }) {
  const e = EDITAL[valor];
  const classe =
    e.classe === "ok" ? css.ok : e.classe === "aviso" ? css.atencao : css.neutro;
  return (
    <span className={`${css.selo} ${classe}`} title={e.titulo}>
      {e.rotulo}
    </span>
  );
}

export function SeloVeto({ motivo }: { motivo?: string | null }) {
  if (!motivo) return null;
  return (
    <span
      className={`${css.selo} ${css.veto}`}
      title={
        `Derrubada antes de baixar o edital porque o objeto contém "${motivo}", ` +
        "que consta nos desqualificadores do perfil. Para reverter, tire a " +
        "palavra de busca.vetos_objeto no perfil.yaml."
      }
    >
      vetada: {motivo}
    </span>
  );
}

/**
 * O que a PESSOA decidiu — distinto do que a IA recomendou.
 *
 * Nada aparece quando está pendente: é o estado da maioria, e um selo "sem
 * decisão" em cada linha da lista viraria ruído em vez de sinal. Quem quer ver
 * o que falta decidir usa a faceta, que conta.
 *
 * Contorno cheio em vez da pílula tênue dos outros selos: esta é a única
 * afirmação da linha que veio de um humano, e ela não pode se perder no meio
 * dos vereditos automáticos.
 */
export function SeloDecisao({ valor }: { valor?: EstadoDecisao | null }) {
  if (!valor || valor === "pendente") return null;
  const sim = valor === "aprovada";
  return (
    <span
      className={`${css.selo} ${css.decidida} ${sim ? css.ok : css.nao}`}
      title={
        sim
          ? "Uma pessoa decidiu participar desta licitação."
          : "Uma pessoa decidiu passar desta licitação."
      }
    >
      {sim ? "vou participar" : "passei"}
    </span>
  );
}

/**
 * O score da etapa 4, com o rótulo junto — número sozinho não diz de quê.
 *
 * O rótulo dizia "aderência" enquanto o campo `score` vinha nulo e o que a
 * lista ordenava era o peso da aderência da triagem. Agora o número é o score
 * calculado, e manter o rótulo antigo faria a tela afirmar que 84 é a
 * aderência que a IA respondeu — que naquela mesma licitação é "media".
 *
 * A COR e o NÚMERO agora contam a mesma história, e isso mudou: a recomendação
 * passou a sair do score (`score >= score.limiar`) em vez da aderência da IA.
 * Enquanto vinha da aderência, um score 84 podia aparecer laranja ao lado de um
 * 73 verde, e a tela não dizia por quê.
 *
 * A discordância que sobrou — score alto com a IA hesitando, ou o inverso —
 * virou `SeloDivergencia`, um selo próprio ao lado. É informação, não ruído, e
 * ganhou lugar visível em vez de ficar implícita numa cor.
 *
 * `—` é score nulo, e é "ninguém pontuou", não "pontuou zero". Zero existe e
 * aparece como 0.
 *
 * E HÁ UM TERCEIRO `—` (D17, 2026-10-05): a faixa `aguardando`. Ali o servidor
 * manda o score nulo de propósito — o número que existe veio da frase do
 * objeto, e a oportunidade espera a leitura integral do edital. A dica diz
 * isso, e não "ainda não passou pela pontuação".
 */
export function Nota({
  valor,
  faixa,
  decisao,
  recomendacao,
  motivo,
}: {
  valor?: number | null;
  faixa?: "avaliar" | "aguardando" | "passar" | "descartar" | null;
  decisao?: "aprovada" | "descartada" | "pendente" | null;
  recomendacao?: Recomendacao | null;
  motivo?: MotivoAba | null;
}) {
  // TRES CORES, TRES SIGNIFICADOS, e o verde e o mais estrito dos tres.
  //
  //   verde   uma PESSOA aprovou. Nenhum score chega aqui sozinho.
  //   ambar   score no topo da faixa — vale o tempo de alguem olhar.
  //   cinza   todo o resto.
  //
  // O verde deixou de ser faixa de score e passou a ser decisao humana. Isso
  // muda o que a cor comunica: ela nao diz mais "o numero e bom", diz "isto
  // ja foi resolvido por gente". E o fim do funil, e e o unico estado que
  // nenhuma aritmetica alcanca — combina com a 0044, em que so a aprovacao
  // humana destrava a leitura paga do edital.
  //
  // `passar` virou cinza junto com o resto: entre o piso e o limiar a
  // licitacao nao pede acao, e dar-lhe cor propria disputava atencao com a
  // faixa que pede.
  //
  // A recomendacao continua visivel no texto do cartao ("Leu o edital
  // inteiro · NO-GO") e na explicacao desta nota, que avisa quando ela
  // discorda do score.
  // O FALLBACK EXISTE POR UMA TELA SO, e esta declarado para nao virar regra
  // paralela: `Editais` monta suas linhas a partir de `EditalEnviado`, que
  // carrega `score` e `recomendacao` mas nao `faixa` nem `decisao`. Sem este
  // ramo, a nota de la ficaria cinza para sempre — regressao silenciosa numa
  // tela que ninguem estava olhando.
  //
  // Quando `EditalEnviado` ganhar os dois campos, este ramo sai e a regra
  // volta a ser uma so.
  const semFaixa = faixa === undefined;
  const classe =
    decisao === "aprovada"
      ? css.notaOk
      : faixa === "avaliar"
        ? css.notaAtencao
        : semFaixa && recomendacao === "participar"
          ? css.notaOk
          : semFaixa && recomendacao === "avaliar"
            ? css.notaAtencao
            : css.notaNao;
  return (
    <span
      className={`${css.nota} ${classe}`}
      title={
        faixa === "aguardando"
          ? "Aguardando leitura do edital: o score sai depois que o edital " +
            "for lido inteiro e cruzado com os atestados."
          : valor == null
          ? "Sem score: esta licitação ainda não passou pela etapa de pontuação."
          : `Score ${valor} de 100, calculado sobre confiança da triagem, ` +
            "afinidade com os atestados, categoria, recência e território." +
            // QUANDO AS DUAS DISCORDAM, a nota diz. É o caso das licitações
            // de score alto cujo edital o sistema leu e recusou: a cor mostra
            // o número, e esta frase mostra o que o documento disse.
            (descartadaPeloFunil(motivo)
              ? ` ${motivo?.texto}: não está em aba nenhuma, e o score não ` +
                "decide mais nada sobre ela."
              : recomendacao === "descartar" && faixa !== "descartar"
              ? " ATENÇÃO: o score é alto, mas o sistema recomenda descartar —" +
                " normalmente porque leu o edital e achou um impeditivo. Veja" +
                " a linha da leitura no cartão."
              : "")
      }
    >
      <b>{valor ?? "—"}</b>
      <span>score</span>
    </span>
  );
}

/**
 * O LOCAL DA OPORTUNIDADE (D23, 2026-10-09). A gestão pediu para "priorizar
 * locais onde o NECTAR já prestou serviços e as capitais ou cidades populares
 * do país". O selo só MOSTRA: não muda faixa, score nem ordem. Quem diz qual é
 * o servidor (`local.selo`): a praça declarada, capital, cidade grande (a
 * lista da configuração) ou UF em que o acervo tem atestado. Nada no resto.
 */
export function SeloLocal({
  local,
}: {
  local?: "praca" | "capital" | "cidade_grande" | "uf_conhecida" | null;
}) {
  if (!local) return null;
  const [texto, dica] = {
    praca: ["praça do Nectar", "UF em que o Nectar executa hoje (a praça declarada na configuração)."],
    capital: ["capital", "Capital de estado: local prioritário para a gestão."],
    cidade_grande: [
      "cidade grande",
      "Cidade da lista de cidades grandes da configuração: local prioritário para a gestão.",
    ],
    uf_conhecida: [
      "UF onde já atuamos",
      "O acervo tem atestado de serviço prestado nesta UF. O acervo guarda a UF, e não o município.",
    ],
  }[local];
  return (
    <span className={`${css.selo} ${css.ok}`} title={dica}>
      {texto}
    </span>
  );
}

/**
 * A REPROVADA POR FALTA DE PARCEIRO (D23): "são boas oportunidades, que só não
 * foram aprovadas por conta de não haver parceiros no local". O servidor lê
 * isso do comentário de quem reprovou; o selo existe para ela ser achada na
 * aba Reprovadas se aparecer parceiro.
 */
export function SeloSemParceiro({ sim }: { sim?: boolean | null }) {
  if (!sim) return null;
  return (
    <span
      className={`${css.selo} ${css.divergencia}`}
      title="Reprovada por falta de parceiro ou de equipe no local, pelo comentário de quem reprovou. O objeto interessa: volta à mesa se aparecer parceiro."
    >
      boa, sem parceiro local
    </span>
  );
}
